/**
 * Moves a camera to where a caller wants it, over time rather than at once.
 *
 * A cut to a framed group is disorienting in a way the same move eased is not: the reader has no
 * way to tell a close look at one part from a different graph entirely, because nothing on screen
 * connects the two pictures. Travelling the distance is what says "this is where you already
 * were" - the one thing a jump cannot.
 *
 * Only the target and the zoom travel. The orientation is the reader's, and a flight that spun the
 * estate to a canonical angle on the way in would take away the thing they had set up.
 *
 * And it gives way. A reader who scrolls on the way in is not asking for a compromise between
 * where they are steering and where the flight was going - they have changed their mind, and four
 * hundred milliseconds is long enough to change it in.
 */

import { useCallback, useEffect, useRef } from 'react';
import type { ViewCamera } from '../types';
import { prefersReducedMotion } from '../reducedMotion';

/**
 * How long a flight takes.
 *
 * Long enough to be followed and short enough not to be waited for. Below about a fifth of a
 * second the eye reads a cut rather than a move, which is the whole thing this exists to avoid;
 * past about three quarters the reader is waiting on an animation to finish before they can look
 * at what they asked for.
 */
const FLIGHT_MS = 420;

/** Decelerating, so the arrival settles rather than stops. */
const easeOut = (at: number): number => 1 - (1 - at) ** 3;

const lerp = (from: number, to: number, at: number): number =>
  from + (to - from) * at;

/**
 * Interpolated in the log domain, because zoom is a ratio rather than a position.
 *
 * Linearly, a flight from 0.5 to 8 spends its first half crossing fifteen sixteenths of the
 * distance: the approach reads as a lurch and then a crawl. Each step being the same *multiple* of
 * the last is what makes a zoom feel even, which is also why the wheel gesture multiplies.
 */
const lerpZoom = (from: number, to: number, at: number): number =>
  Math.exp(lerp(Math.log(from), Math.log(to), at));

export type CameraFlight = {
  /** Flies to `goal`, replacing any flight already under way. */
  flyTo: (goal: ViewCamera) => void;
};

export type CameraFlightArgs = {
  /** The camera in effect, which is where a flight departs from. */
  camera: ViewCamera;
  applyCamera: (next: ViewCamera) => void;
  /**
   * Whether the reader has taken the camera, read between frames rather than between renders.
   *
   * The flight's own frames clear this as they go - they set the camera, and that is what the
   * flag is measured from - so what it reports in the middle of a flight is exactly "the reader
   * has moved it since my last frame", which is the question being asked.
   */
  hasReaderTakenCamera: () => boolean;
};

export const useCameraFlight = ({
  camera,
  applyCamera,
  hasReaderTakenCamera,
}: CameraFlightArgs): CameraFlight => {
  /* Read through refs so a flight already in the air is not restarted by an unrelated re-render,
     and so the one starting now departs from where the camera actually is. */
  const live = useRef(camera);
  live.current = camera;
  const apply = useRef(applyCamera);
  apply.current = applyCamera;
  const hasReaderTaken = useRef(hasReaderTakenCamera);
  hasReaderTaken.current = hasReaderTakenCamera;

  const frame = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      if (frame.current !== undefined) cancelAnimationFrame(frame.current);
    },
    []
  );

  const flyTo = useCallback((goal: ViewCamera) => {
    if (frame.current !== undefined) cancelAnimationFrame(frame.current);

    /* Arriving is the point; the travelling is a courtesy. A reader who has asked for no motion
       gets the destination, not a shorter flight. */
    if (prefersReducedMotion()) {
      apply.current(goal);
      return;
    }

    const from = live.current;
    const startedAt = performance.now();

    /* Departing is itself a claim on the camera, which clears the flag the frames below read. A
       reader who had already turned the estate has not been overridden by their own menu choice,
       and without this the first frame would read their earlier gesture as an interruption and
       the flight would never leave. Writing the camera it already has moves nothing. */
    apply.current(from);

    const step = (now: number) => {
      /* The reader is steering; stop rather than steer against them. */
      if (hasReaderTaken.current()) {
        frame.current = undefined;
        return;
      }

      const at = Math.min(1, (now - startedAt) / FLIGHT_MS);
      const eased = easeOut(at);

      /* Spread from the live camera rather than from the goal, so the flight writes only what it
         travels. Spread from the goal it would also write the orientation captured when the
         flight began, and a reader who turns the estate on the way in would find each frame
         putting it back. */
      apply.current({
        ...live.current,
        target: [
          lerp(from.target[0], goal.target[0], eased),
          lerp(from.target[1], goal.target[1], eased),
          lerp(from.target[2], goal.target[2], eased),
        ],
        zoom: lerpZoom(from.zoom, goal.zoom, eased),
      });

      frame.current = at < 1 ? requestAnimationFrame(step) : undefined;
    };

    frame.current = requestAnimationFrame(step);
  }, []);

  return { flyTo };
};
